import { formatSEK } from '@budgie/shared';
import { ChevronDown } from 'lucide-react';
import { Fragment, type MouseEvent } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SortableHead } from '@/components/ui/sortable-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { sortItems, useTableSort } from '@/lib/useTableSort';
import { cn } from '@/lib/utils';
import { trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { EmptyState } from '../common/EmptyState';
import { BillHistory } from './BillHistory';

type BillItem = {
  id: string;
  name: string;
  isActive: boolean;
  lastAmount: number | null;
  lastDate: string | null;
  lastAccountName: string | null;
  totalPayments: number;
  ruleCount: number;
  billMatchConflicts: number;
  prediction: {
    amount: number;
    confidence: 'low' | 'medium' | 'high';
    dataPoints: number;
    trend: 'stable' | 'increasing' | 'decreasing';
  } | null;
};

type BillConflict = {
  transactionId: string;
  description: string;
  date: string;
  amount: number;
  accountName: string | null;
  assignedBill: {
    id: string;
    name: string;
  };
  conflictingBill: {
    id: string;
    name: string;
  };
};

type Props = {
  bills: BillItem[];
  selectedBillId: string | null;
  selectedBillConflicts: BillConflict[] | undefined;
  selectedBillConflictsLoading: boolean;
  selectedBillConflictsError: string | null;
  onSelectBill: (billId: string | null) => void;
  onCreate: () => void;
  onEdit: (bill: Pick<BillItem, 'id' | 'name'>) => void;
};

const CONFIDENCE_LABELS = {
  low: 'Låg säkerhet',
  medium: 'Medel',
  high: 'Hög säkerhet',
} as const;

const TREND_ICONS = {
  stable: '→',
  increasing: '↑',
  decreasing: '↓',
} as const;

function BillBadges({ bill }: { bill: BillItem }) {
  return (
    <>
      {!bill.isActive && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
          Inaktiv
        </Badge>
      )}
      {bill.billMatchConflicts > 0 && (
        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
          {bill.billMatchConflicts} konflikter
        </Badge>
      )}
    </>
  );
}

export function BillsList({
  bills,
  selectedBillId,
  selectedBillConflicts,
  selectedBillConflictsLoading,
  selectedBillConflictsError,
  onSelectBill,
  onCreate,
  onEdit,
}: Props) {
  const { confirm } = useConfirm();
  const { sort, toggle } = useTableSort<string>();
  const utils = trpc.useUtils();

  const deleteMutation = trpc.recurringBill.delete.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.recurringBill.list.invalidate(),
        utils.recurringBill.conflicts.invalidate(),
        utils.recurringBill.predict.invalidate(),
        utils.recurringBill.history.invalidate(),
      ]);
      toast.success('Räkningen togs bort');
      onSelectBill(null);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = async (bill: BillItem) => {
    const ok = await confirm({
      title: `Ta bort "${bill.name}"?`,
      message:
        'Historik och kopplade regler tas bort från räkningen. Själva transaktionerna påverkas inte.',
      confirmLabel: 'Ta bort',
      destructive: true,
    });

    if (!ok) return;

    deleteMutation.mutate({ id: bill.id });
  };

  const toggleSelected = (bill: BillItem) => {
    onSelectBill(selectedBillId === bill.id ? null : bill.id);
  };

  const editClick = (bill: BillItem) => (e: MouseEvent) => {
    e.stopPropagation();
    onEdit({
      id: bill.id,
      name: bill.name,
    });
  };

  const deleteClick = (bill: BillItem) => (e: MouseEvent) => {
    e.stopPropagation();
    void handleDelete(bill);
  };

  const renderDetails = (bill: BillItem) => (
    <div className="space-y-4">
      <BillHistory billId={bill.id} />

      {bill.billMatchConflicts > 0 && (
        <Card size="sm">
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Matchningskonflikter</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedBillConflictsLoading ? (
              <div className="text-sm text-muted-foreground">Laddar konflikter…</div>
            ) : selectedBillConflictsError ? (
              <EmptyState
                icon="⚠️"
                title="Kunde inte hämta konflikter"
                description={selectedBillConflictsError}
              />
            ) : !selectedBillConflicts || selectedBillConflicts.length === 0 ? (
              <EmptyState
                icon="✅"
                title="Inga konflikter att granska"
                description="Alla matchande transaktioner är kopplade till rätt räkning just nu."
              />
            ) : (
              <div className="space-y-3">
                {selectedBillConflicts.map((conflict) => (
                  <div
                    key={conflict.transactionId}
                    className="rounded-xl border border-border px-4 py-3"
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0 space-y-1">
                        <div className="break-words font-medium text-foreground">
                          {conflict.description}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {conflict.date}
                          {conflict.accountName ? ` · ${conflict.accountName}` : ''}
                        </div>
                        <div className="text-sm text-warning">
                          Redan kopplad till{' '}
                          <span className="font-medium">{conflict.assignedBill.name}</span>
                        </div>
                      </div>
                      <div className="tabular-nums text-foreground md:text-right">
                        {formatSEK(conflict.amount)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const sortedBills = sortItems(bills, sort, {
    name: (b) => b.name,
    account: (b) => b.lastAccountName ?? '',
    lastAmount: (b) => b.lastAmount ?? 0,
    prediction: (b) => b.prediction?.amount ?? 0,
    payments: (b) => b.totalPayments,
  });

  const totalLastAmount = sortedBills.reduce((sum, bill) => sum + (bill.lastAmount ?? 0), 0);
  const totalPrediction = sortedBills.reduce(
    (sum, bill) => sum + (bill.prediction?.amount ?? 0),
    0,
  );

  if (bills.length === 0) {
    return (
      <EmptyState
        icon="📬"
        title="Inga räkningar"
        description="Skapa din första räkning för att börja följa kostnader månad för månad."
        action={{
          label: 'Skapa räkning',
          onClick: onCreate,
        }}
      />
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card">
      <header className="flex items-baseline justify-between gap-3 px-4 pt-4 pb-3 sm:px-5">
        <h2 className="text-[15px] font-semibold">
          {bills.length} {bills.length === 1 ? 'räkning' : 'räkningar'}
        </h2>
        <p className="text-sm text-muted-foreground tabular-nums">
          Prognos{' '}
          <span className="font-semibold text-foreground">{formatSEK(totalPrediction)}</span>
        </p>
      </header>

      {/* Mobile: row list, tap a row for history and actions */}
      <ul className="divide-y divide-border border-t border-border md:hidden">
        {sortedBills.map((bill) => {
          const isSelected = selectedBillId === bill.id;

          return (
            <li key={bill.id}>
              <button
                type="button"
                className="flex min-h-14 w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-inset"
                aria-expanded={isSelected}
                onClick={() => toggleSelected(bill)}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="break-words font-medium">{bill.name}</span>
                    <BillBadges bill={bill} />
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {bill.lastAmount != null ? (
                      <>
                        Senast {formatSEK(bill.lastAmount)}
                        {bill.lastDate ? ` · ${bill.lastDate}` : ''}
                        {bill.lastAccountName ? ` · ${bill.lastAccountName}` : ''}
                      </>
                    ) : (
                      'Ingen betalning ännu'
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-start gap-1.5">
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums">
                      {bill.prediction ? formatSEK(bill.prediction.amount) : '—'}
                    </div>
                    {bill.prediction && (
                      <div className="text-xs text-muted-foreground">
                        {TREND_ICONS[bill.prediction.trend]}{' '}
                        {CONFIDENCE_LABELS[bill.prediction.confidence]}
                      </div>
                    )}
                  </div>
                  <ChevronDown
                    aria-hidden="true"
                    className={cn(
                      'mt-0.5 size-4 text-muted-foreground transition-transform',
                      isSelected && 'rotate-180',
                    )}
                  />
                </div>
              </button>
              {isSelected && (
                <div className="space-y-3 bg-muted/40 px-4 pt-1 pb-4">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 flex-1"
                      onClick={editClick(bill)}
                    >
                      Redigera
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 flex-1 text-destructive hover:text-destructive"
                      disabled={deleteMutation.isPending}
                      onClick={deleteClick(bill)}
                    >
                      Ta bort
                    </Button>
                  </div>
                  {renderDetails(bill)}
                </div>
              )}
            </li>
          );
        })}
        <li className="flex items-center justify-between gap-3 bg-muted/40 px-4 py-3 text-sm font-semibold">
          <span>Totalt</span>
          <span className="text-right tabular-nums">
            <span className="block">{formatSEK(totalPrediction)}</span>
            <span className="block text-xs font-normal text-muted-foreground">
              Senast {formatSEK(totalLastAmount)}
            </span>
          </span>
        </li>
      </ul>

      {/* Desktop: table */}
      <div className="hidden border-t border-border md:block [&_td:first-child]:pl-5 [&_td:last-child]:pr-5 [&_th:first-child]:pl-5 [&_th:last-child]:pr-5">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableHead sortKey="name" sort={sort} onToggle={toggle}>
                Namn
              </SortableHead>
              <SortableHead sortKey="account" sort={sort} onToggle={toggle}>
                Dras från
              </SortableHead>
              <SortableHead
                sortKey="lastAmount"
                sort={sort}
                onToggle={toggle}
                className="text-right"
              >
                Senast
              </SortableHead>
              <SortableHead
                sortKey="prediction"
                sort={sort}
                onToggle={toggle}
                className="text-right"
              >
                Prognos
              </SortableHead>
              <SortableHead sortKey="payments" sort={sort} onToggle={toggle} className="text-right">
                Betalningar
              </SortableHead>
              <TableHead className="text-right">
                <span className="sr-only">Åtgärder</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedBills.map((bill) => {
              const isSelected = selectedBillId === bill.id;

              return (
                <Fragment key={bill.id}>
                  <TableRow
                    className="cursor-pointer"
                    data-state={isSelected ? 'selected' : undefined}
                    onClick={() => toggleSelected(bill)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{bill.name}</span>
                        <BillBadges bill={bill} />
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {bill.lastAccountName ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {bill.lastAmount != null ? (
                        <div>
                          <div className="text-sm tabular-nums">{formatSEK(bill.lastAmount)}</div>
                          {bill.lastDate && (
                            <div className="text-xs text-muted-foreground tabular-nums">
                              {bill.lastDate}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {bill.prediction ? (
                        <div>
                          <div className="text-sm font-medium tabular-nums">
                            {formatSEK(bill.prediction.amount)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {TREND_ICONS[bill.prediction.trend]}{' '}
                            {CONFIDENCE_LABELS[bill.prediction.confidence]}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground tabular-nums">
                      {bill.totalPayments}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={editClick(bill)}
                        >
                          Redigera
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                          disabled={deleteMutation.isPending}
                          onClick={deleteClick(bill)}
                        >
                          Ta bort
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {isSelected && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={6} className="bg-muted/40 p-4 whitespace-normal">
                        {renderDetails(bill)}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            <TableRow className="bg-muted/40 font-semibold hover:bg-muted/40">
              <TableCell>Totalt</TableCell>
              <TableCell />
              <TableCell className="text-right text-sm tabular-nums">
                {formatSEK(totalLastAmount)}
              </TableCell>
              <TableCell className="text-right text-sm tabular-nums">
                {formatSEK(totalPrediction)}
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
